// voice-lint-disable-next-line
/* eslint-disable react/no-unknown-property */
import React, { useMemo } from "react";
import { SUPPLIERS, suppliersFor } from "../../lib/content/suppliers";

/**
 * SupplierMarquee
 * ----------------------------------------------------------------------
 *  Cinematic, calm B2B credibility strip.  Renders each supplier as a
 *  brand-flavoured monochrome wordmark inside a slow horizontal scroll.
 *
 *  Props
 *    category   string | undefined   — service slug to filter ('pumps',
 *                                       'purification', etc.).  If omitted,
 *                                       all suppliers are shown.
 *    tone       'light' | 'dark'     — surface colour theme.  Defaults to
 *                                       'light' (warm white).
 *    eyebrow    string               — small uppercase line above strip.
 *    title      string               — main headline next to it.
 *    speed      number (s)           — scroll duration.  Larger = slower.
 *
 *  Rationale: we deliberately render wordmarks instead of hot-linking
 *  partner logos so the page stays trademark-safe, vector-perfect at any
 *  size, and offline-resilient.  Brand recognition is preserved through
 *  typographic treatment per supplier.
 * ----------------------------------------------------------------------
 */
export default function SupplierMarquee({
  category,
  tone = "light",
  speed = 60,
}) {
  const list = useMemo(() => suppliersFor(category), [category]);

  // Need at least ~6 entries for a marquee to feel alive — duplicate if
  // a single category returns fewer brands so the scroll never gaps.
  const padded = useMemo(() => {
    if (list.length >= 8) return list;
    const copies = Math.ceil(8 / Math.max(list.length, 1));
    return Array.from({ length: copies }).flatMap(() => list);
  }, [list]);

  // Two copies of the list for the seamless infinite scroll trick.
  const track = [...padded, ...padded];

  const palette = tone === "dark"
    ? { bg: "#0B1014", fg: "rgba(245,247,250,0.72)", fgHover: "#F5F7FA", border: "rgba(255,255,255,0.08)", eyebrowFg: "#1FB6C1", titleFg: "#F5F7FA", fadeFrom: "#0B1014" }
    : { bg: "#FAFAF7", fg: "rgba(12,42,64,0.62)",   fgHover: "#0C2A40", border: "rgba(12,42,64,0.08)",   eyebrowFg: "#1FB6C1", titleFg: "#0C2A40", fadeFrom: "#FAFAF7" };

  return (
    <section
      className="relative overflow-hidden border-y"
      style={{ background: palette.bg, borderColor: palette.border, color: palette.fg }}
      data-testid={`supplier-marquee${category ? `-${category}` : ""}`}
      aria-label="Equipment partners"
    >
      {/* Header band */}
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 pt-10 md:pt-12 pb-6 md:pb-8">
        <div
          className="font-mono uppercase"
          style={{ fontSize: "0.62rem", letterSpacing: "0.26em", color: palette.eyebrowFg }}
        >
          Our partners
        </div>
      </div>

      {/* Marquee track */}
      <div
        className="hv-marquee"
        style={{
          "--hv-marquee-fade": palette.fadeFrom,
          "--hv-marquee-duration": `${speed}s`,
        }}
      >
        <ul className="hv-marquee-track" style={{ animationDuration: `${speed}s` }}>
          {track.map((s, i) => (
            <li key={`${s.id}-${i}`} className="hv-marquee-item">
              <span
                className="hv-supplier-mark"
                style={{
                  fontFamily: s.family,
                  fontWeight: s.weight,
                  letterSpacing: s.tracking,
                  fontStyle: s.italic ? "italic" : "normal",
                  textTransform: s.case === "upper" ? "uppercase" : (s.case === "lower" ? "lowercase" : "none"),
                  color: palette.fg,
                  "--hv-supplier-hover": palette.fgHover,
                }}
                aria-label={s.name}
              >
                {s.label}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="pb-8 md:pb-10" />
    </section>
  );
}
